import React, { FC, useState, useMemo } from 'react'
import type { QhseAuditKind, VesselLite } from '../core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import ReactSelect, { MultiValue, StylesConfig } from 'react-select'

type Option = { value: string; label: string }
const ALL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

interface MultiSelectProps {
  label: string
  options: Option[]
  selected: string[]
  onChange: (v: string[]) => void
  error?: string
}

const LABEL_CLS = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
}

const MultiSelect: FC<MultiSelectProps> = ({ label, options, selected, onChange, error }) => {
  const allSelected = options.length > 0 && selected.length === options.length

  const optionsWithSelectAll: Option[] = useMemo(() => {
    if (options.length === 0) return []
    return [
      { value: '__SELECT_ALL__', label: allSelected ? '✓ Deselect All' : 'Select All' },
      ...options,
    ]
  }, [options, allSelected])

  const selectedOptions = useMemo(() => {
    return options.filter(o => selected.includes(o.value))
  }, [options, selected])

  const handleChange = (newVals: MultiValue<Option>) => {
    const clickedSelectAll = newVals.some(v => v.value === '__SELECT_ALL__')
    if (clickedSelectAll) {
      if (allSelected) {
        onChange([])
      } else {
        onChange(options.map(o => o.value))
      }
      return
    }
    onChange(newVals.map(v => v.value))
  }

  const customStyles: StylesConfig<Option, true> = {
    control: (base, state) => ({
      ...base,
      borderColor: error ? '#dc3545' : state.isFocused ? '#3b82f6' : '#E4E6EF',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(59,130,246,.25)' : 'none',
      minHeight: 38,
      maxHeight: 120,
      overflowY: 'auto',
      borderRadius: 8,
      '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#E4E6EF' },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10000,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      zIndex: 10000,
    }),
    option: (base, state) => {
      const isSelectAllOption = state.data.value === '__SELECT_ALL__'
      return {
        ...base,
        backgroundColor: state.isSelected
          ? '#3b82f6'
          : state.isFocused
            ? '#eff6ff'
            : isSelectAllOption
              ? '#f8f9fa'
              : 'white',
        color: state.isSelected
          ? 'white'
          : isSelectAllOption
            ? '#2563eb'
            : '#1e293b',
        fontWeight: isSelectAllOption ? 600 : 400,
        borderBottom: isSelectAllOption ? '1px solid #E4E6EF' : 'none',
        '&:active': { backgroundColor: state.isSelected ? '#2563eb' : '#dbeafe' },
        cursor: 'pointer',
        fontSize: 14,
      }
    },
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: 14 }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#eff6ff',
      borderRadius: 6,
      border: '1px solid #bfdbfe',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#1e40af',
      fontSize: 13,
      fontWeight: 500,
      padding: '2px 6px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#1e40af',
      '&:hover': { backgroundColor: '#fee2e2', color: '#ef4444' },
    }),
    input: (base) => ({ ...base, fontSize: 14 }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      color: '#94a3b8',
      '&:hover': { color: '#64748b' },
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      cursor: 'pointer',
      color: state.isFocused ? '#3b82f6' : '#94a3b8',
      '&:hover': { color: '#3b82f6' },
    }),
  }

  return (
    <div>
      <ReactSelect<Option, true>
        isMulti
        closeMenuOnSelect={false}
        classNamePrefix='react-select'
        options={optionsWithSelectAll}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={`Select ${label}`}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPosition='fixed'
        isClearable
      />
      {error && <div className='invalid-feedback d-block mt-1 fs-7'>{error}</div>}
    </div>
  )
}

type NewPlanData = {
  auditKindId: number
  vesselIds: number[]
  months: number[] // 1..12
  year: number
}

interface AddAuditPlanModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: NewPlanData, saveAndAddNew: boolean) => void
  kinds: QhseAuditKind[]
  vessels: VesselLite[]
}

const AddAuditPlanModal: FC<AddAuditPlanModalProps> = ({
  visible,
  onClose,
  onSubmit,
  kinds,
  vessels,
}) => {
  const [year, setYear] = useState<number>(new Date().getFullYear())
  const [auditKind, setAuditKind] = useState<string>('')
  const [selectedVesselIds, setSelectedVesselIds] = useState<string[]>([])
  const [selectedMonthLabels, setSelectedMonthLabels] = useState<string[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const vesselOptions: Option[] = vessels.map(v => ({
    value: String(v.id),
    label: v.name,
  }))
  const monthOptions: Option[] = (ALL_MONTHS as readonly string[]).map(m => ({
    value: m,
    label: m,
  }))

  const reset = () => {
    setAuditKind('')
    setSelectedVesselIds([])
    setSelectedMonthLabels([])
    setErrors({})
  }

  const close = () => {
    reset()
    onClose()
  }

  const validate = () => {
    const er: Record<string, string> = {}
    if (!auditKind) er.auditKind = 'Audit is required'
    if (selectedVesselIds.length === 0) er.vessels = 'Select at least one vessel'
    if (selectedMonthLabels.length === 0) er.months = 'Select at least one month'
    setErrors(er)
    return Object.keys(er).length === 0
  }

  const submit = (saveAndAddNew: boolean) => {
    if (!validate()) return
    const months = selectedMonthLabels
      .map(m => ALL_MONTHS.indexOf(m as any) + 1)
      .filter(n => n > 0)

    onSubmit(
      {
        year,
        auditKindId: Number(auditKind),
        vesselIds: selectedVesselIds.map(Number),
        months,
      },
      saveAndAddNew
    )

    if (saveAndAddNew) {
      reset()
    } else {
      close()
    }
  }

  if (!visible) return null

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      style={WRAP_STYLE}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered'>
        <div className='modal-content bg-white text-dark'>
          <div className='modal-header'>
            <h5 className='modal-title text-dark'>Add Audit Plan</h5>
            <button type='button' className='btn-close' onClick={close} />
          </div>
          <div className='modal-body'>
            <div className='row g-5'>
              <div className='col-12'>
                <label className={LABEL_CLS}>Year</label>
                <SearchableSelect
                  className='form-select text-dark'
                  value={year}
                  onChange={e => setYear(parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(
                    y => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    )
                  )}
                </SearchableSelect>
              </div>

              <div className='col-12'>
                <label className={LABEL_CLS}>
                  Audit <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  className={`form-select text-dark ${errors.auditKind ? 'is-invalid' : ''
                    }`}
                  value={auditKind}
                  onChange={e => setAuditKind(e.target.value)}
                >
                  <option value=''>Select Audit</option>
                  {kinds.map(k => (
                    <option key={k.id} value={String(k.id)}>
                      {k.name}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.auditKind && (
                  <div className='invalid-feedback'>{errors.auditKind}</div>
                )}
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>
                  Vessels <span className='text-danger'>*</span>
                </label>
                <MultiSelect
                  label='Vessels'
                  options={vesselOptions}
                  selected={selectedVesselIds}
                  onChange={setSelectedVesselIds}
                  error={errors.vessels}
                />
              </div>

              <div className='col-md-6'>
                <label className={LABEL_CLS}>
                  Months <span className='text-danger'>*</span>
                </label>
                <MultiSelect
                  label='Months'
                  options={monthOptions}
                  selected={selectedMonthLabels}
                  onChange={setSelectedMonthLabels}
                  error={errors.months}
                />
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-light btn-sm'
              onClick={close}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary btn-sm'
              onClick={() => submit(true)}
            >
              Save & Add New
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={() => submit(false)}
            >
              Save & Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddAuditPlanModal
